/* MC Collection (native) — custom element <mc-collection>, rendered by sections/mc-collection-native.liquid.
   Every filter / sort / page change re-renders the section through the Section Rendering API (one request) and
   swaps the chips, results and pager regions; the drawer is patched in place so it stays open with its groups,
   scroll positions and focus intact. URLs carry only Shopify's own params (filter.*, sort_by, page), so reloads,
   shared links and back/forward render the same page server-side. No Prefixbox, no theme section framework. */
(() => {
  'use strict';
  if (window.customElements.get('mc-collection')) return;

  const CACHE_MAX = 24;
  const IMG_WAIT_MS = 1200;   // like the old ghost: wait for the first 10 images, never longer than this
  const RETURN_KEY = 'mc-cr-return';
  const CHEVRON = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const isShopifyKey = (k) => k === 'sort_by' || k === 'page' || k.startsWith('filter.');

  // Query string for a state: Shopify params sorted (stable cache key / URL), page=1 dropped; every other param
  // already in the address bar (utm_*, gclid, preview_theme_id …) is carried over so tracking survives filtering.
  function buildQuery(shopifyParams) {
    const extra = [];
    new URLSearchParams(location.search).forEach((v, k) => { if (!isShopifyKey(k) && k !== 'section_id') extra.push([k, v]); });
    const own = [];
    shopifyParams.forEach((v, k) => { if (isShopifyKey(k) && v !== '' && !(k === 'page' && v === '1')) own.push([k, v]); });
    own.sort((a, b) => (a[0] === b[0] ? (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0) : a[0] < b[0] ? -1 : 1));
    const out = new URLSearchParams();
    extra.concat(own).forEach(([k, v]) => out.append(k, v));
    return out.toString();
  }

  class McCollection extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.sectionId = this.dataset.sectionId;
      this.drawer = this.querySelector('[data-mc-drawer]');
      this.form = this.querySelector('[data-mc-form]');
      this.live = this.querySelector('[data-mc-live]');
      this.cache = new Map();
      this.token = 0;
      this.key = buildQuery(new URLSearchParams(location.search));
      this.cache.set(this.key, this.outerHTML);                  // back to the landing state needs no request

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onFormClick = this.onFormClick.bind(this);
      this.onPop = () => this.go(new URLSearchParams(location.search), { push: false });

      this.addEventListener('click', this.onClick);
      if (this.form) {
        this.form.addEventListener('change', this.onChange);
        this.form.addEventListener('pointerdown', this.onPointerDown, true);
        this.form.addEventListener('click', this.onFormClick);
        this.form.addEventListener('submit', (e) => { e.preventDefault(); this.applyForm(); });
      }
      if (this.drawer) this.drawer.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });   // Escape
      window.addEventListener('popstate', this.onPop);

      this.buildAbout();
      this.returnToCard();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
      document.documentElement.classList.remove('mc-cr--drawer-open');
    }

    // ── delegated clicks: toolbar, drawer chrome, chips / pager / empty-state links ──
    onClick(e) {
      const t = e.target;
      const opener = t.closest('[data-mc-open]');
      if (opener) { this.open(opener.dataset.mcOpen, opener); return; }
      if (t.closest('[data-mc-close]')) { this.close(); return; }
      const gh = t.closest('.mc-cr-drawer__gh');
      if (gh) { const g = gh.closest('.mc-cr-drawer__group'); this.setGroup(g, !g.classList.contains('is-open')); return; }
      if (t.closest('[data-mc-clear-drawer]')) { this.clearDrawer(); return; }
      const a = t.closest('a[data-mc-nav]');
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      this.go(new URL(a.href, location.href).searchParams, { scroll: !!a.closest('[data-mc-region="pager"]') });
    }

    // ── drawer: open / close / groups ──
    open(mode, trigger) {
      if (!this.drawer || this.drawer.open) return;
      this.trigger = trigger;
      const groups = [...this.form.querySelectorAll('.mc-cr-drawer__group')];
      groups.forEach((g) => this.setGroup(g, false));
      const want = mode === 'sort' ? this.form.querySelector('[data-group="sort"]') : (this.form.querySelector('[data-group="fcat"]') || groups[0]);
      if (want) this.setGroup(want, true);
      this.drawer.showModal();
      document.documentElement.classList.add('mc-cr--drawer-open');
      this.form.scrollTop = 0;
      requestAnimationFrame(() => requestAnimationFrame(() => this.drawer.classList.add('is-open')));   // slide-in
      if (mode === 'sort' && want) setTimeout(() => want.scrollIntoView({ block: 'end' }), 60);
      trigger.setAttribute('aria-expanded', 'true');
      this.drawer.querySelector('.mc-cr-drawer__close').focus();
    }

    close() {
      if (!this.drawer || !this.drawer.open) return;
      this.drawer.classList.remove('is-open');
      document.documentElement.classList.remove('mc-cr--drawer-open');
      setTimeout(() => {
        this.drawer.close();
        if (this.trigger) { this.trigger.setAttribute('aria-expanded', 'false'); this.trigger.focus(); }
      }, 250);                                                    // the panel's .25s transform transition
    }

    setGroup(g, open) {
      if (!g) return;
      g.classList.toggle('is-open', open);
      g.querySelector('.mc-cr-drawer__gh').setAttribute('aria-expanded', String(open));
    }

    // Single-select groups: clicking the picked value again clears it (today's behaviour). The browser fires no
    // change for an already-checked radio, so remember the state at pointerdown and clear it on the input's click.
    onPointerDown(e) {
      const opt = e.target.closest('.mc-cr-drawer__opt');
      const input = opt && opt.querySelector('input[type="radio"]');
      this._was = input && input.checked && input.closest('[data-mc-toggle]') ? input : null;
    }
    onFormClick(e) {
      const input = e.target.closest('input[type="radio"]');   // a label click re-dispatches a click on its input
      if (!input) return;
      if (input === this._was) { input.checked = false; input.dispatchEvent(new Event('change', { bubbles: true })); }
      this._was = null;
    }

    onChange(e) {
      const input = e.target;
      if (!input.matches('input')) return;
      if (input.name === 'mc-price') this.form.querySelectorAll('[data-mc-custom-price]').forEach((n) => n.remove());
      this.syncGroup(input.closest('.mc-cr-drawer__group'));
      this.drawer.classList.add('is-updating');
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this.applyForm(), 150);   // arrow keys in a radiogroup = one request
    }

    // optimistic UI: marks + hint follow the inputs at once; the response confirms them a moment later
    syncGroup(g) {
      if (!g) return;
      const inputs = [...g.querySelectorAll('input')];
      inputs.forEach((i) => i.closest('.mc-cr-drawer__opt').classList.toggle('is-checked', i.checked));
      const picked = inputs.filter((i) => i.checked).map((i) => i.closest('.mc-cr-drawer__opt').querySelector('.mc-cr-drawer__olabel').textContent);
      g.querySelector('[data-mc-hint]').textContent = picked.length ? picked.join(', ') : g.dataset.hint;
    }

    // Drawer "Clear all": filters and price go, sort stays (chips' "Clear all" is a link that drops sort too).
    clearDrawer() {
      this.form.querySelectorAll('.mc-cr-drawer__group:not([data-group="sort"]) input').forEach((i) => { i.checked = i.name === 'mc-price' && i.value === 'any'; });
      this.form.querySelectorAll('[data-mc-custom-price], [data-mc-extra]').forEach((n) => n.remove());
      this.form.querySelectorAll('.mc-cr-drawer__group').forEach((g) => this.syncGroup(g));
      this.drawer.classList.add('is-updating');
      this.applyForm();
    }

    paramsFromForm() {
      const p = new URLSearchParams();
      for (const [k, v] of new FormData(this.form)) if (k !== 'mc-price' && v !== '') p.append(k, v);
      const price = this.form.querySelector('input[name="mc-price"]:checked');
      const pg = this.form.querySelector('[data-group="price"]');
      if (price && pg) {
        if (price.dataset.gte) p.append(pg.dataset.gteName, price.dataset.gte);
        if (price.dataset.lte) p.append(pg.dataset.lteName, price.dataset.lte);
      }
      return p;                                                   // no page: every filter / sort change starts at page 1
    }
    applyForm() { this.go(this.paramsFromForm()); }

    // ── one state change: URL → section HTML → swap ──
    async go(params, { push = true, scroll = false } = {}) {
      const qs = buildQuery(params);
      const url = location.pathname + (qs ? '?' + qs : '');
      if (push && qs !== buildQuery(new URLSearchParams(location.search))) history.pushState({ mc: 1 }, '', url);
      if (scroll) this.scrollToToolbar();
      if (qs === this.key) {                                      // already on screen (e.g. back to it mid-request)
        this.token++;
        if (this.controller) this.controller.abort();
        this.setBusy(false);
        return;
      }
      const token = ++this.token;
      this.setBusy(true);
      try {
        const html = await this.load(qs);
        if (token !== this.token) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        await this.preload(doc);
        if (token !== this.token) return;
        this.render(doc, qs);
        this.setBusy(false);
      } catch (err) {
        if (token !== this.token || (err && err.name === 'AbortError')) return;
        location.assign(url);                                     // network / HTTP error: let the server render it
      }
    }

    async load(qs) {
      if (this.controller) this.controller.abort();
      if (this.cache.has(qs)) return this.cache.get(qs);
      this.controller = new AbortController();
      const res = await fetch(location.pathname + '?' + qs + (qs ? '&' : '') + 'section_id=' + encodeURIComponent(this.sectionId), { signal: this.controller.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error('Section request failed: ' + res.status);
      const html = await res.text();
      this.cache.set(qs, html);
      if (this.cache.size > CACHE_MAX) this.cache.delete(this.cache.keys().next().value);
      return html;
    }

    // Warm the first 10 card images (same srcset/sizes → same candidate) so the swap shows a finished grid.
    preload(doc) {
      const imgs = [...doc.querySelectorAll('[data-mc-region="results"] .mc-cr-card__img:not(.mc-cr-card__img--alt)')].slice(0, 10);
      if (!imgs.length) return Promise.resolve();
      const jobs = imgs.map((src) => {
        const i = new Image();
        i.sizes = src.getAttribute('sizes') || '';
        i.srcset = src.getAttribute('srcset') || '';
        i.src = src.getAttribute('src');
        return i.decode().catch(() => {});
      });
      return Promise.race([Promise.all(jobs), new Promise((r) => setTimeout(r, IMG_WAIT_MS))]);
    }

    render(doc, qs) {
      ['chips', 'results', 'pager'].forEach((name) => {
        const next = doc.querySelector('[data-mc-region="' + name + '"]');
        const cur = this.querySelector('[data-mc-region="' + name + '"]');
        if (next && cur) cur.replaceWith(document.importNode(next, true));
      });
      this.patchDrawer(doc.querySelector('[data-mc-drawer]'));
      this.key = qs;
      const total = parseInt(this.querySelector('[data-mc-region="results"]').dataset.mcTotal, 10) || 0;
      if (this.live) this.live.textContent = total + ' product' + (total === 1 ? '' : 's');
      document.dispatchEvent(new CustomEvent('mc:collection:updated', { detail: { url: location.href, total } }));
    }

    // Patch, don't replace: keeps the dialog open, its open groups, list scroll and the focused option.
    patchDrawer(next) {
      if (!next || !this.form) return;
      const nextForm = next.querySelector('[data-mc-form]');
      const ids = (f) => [...f.querySelectorAll('.mc-cr-drawer__group')].map((g) => g.dataset.group).join();
      if (ids(this.form) !== ids(nextForm)) {                    // group set changed (rare): rebuild, keep open state
        const open = [...this.form.querySelectorAll('.mc-cr-drawer__group.is-open')].map((g) => g.dataset.group);
        this.form.innerHTML = nextForm.innerHTML;
        open.forEach((id) => this.setGroup(this.form.querySelector('[data-group="' + id + '"]'), true));
      } else {
        this.form.querySelectorAll('.mc-cr-drawer__group').forEach((g) => {
          const ng = nextForm.querySelector('[data-group="' + g.dataset.group + '"]');
          const opts = g.querySelector('.mc-cr-drawer__opts');
          const nopts = ng.querySelector('.mc-cr-drawer__opts');
          const sig = (el) => [...el.querySelectorAll('input')].map((i) => i.name + '=' + i.value).join('\n');
          if (sig(opts) !== sig(nopts)) {
            const focused = opts.contains(document.activeElement) ? document.activeElement.value : null;
            const top = opts.scrollTop;
            opts.innerHTML = nopts.innerHTML;
            opts.scrollTop = top;
            if (focused !== null) { const f = [...opts.querySelectorAll('input')].find((i) => i.value === focused); if (f) f.focus(); }
          } else {
            const ni = nopts.querySelectorAll('input');
            opts.querySelectorAll('input').forEach((input, i) => {
              input.checked = ni[i].hasAttribute('checked');
              input.disabled = ni[i].hasAttribute('disabled');
              const row = input.closest('.mc-cr-drawer__opt');
              row.classList.toggle('is-checked', input.checked);
              row.classList.toggle('is-disabled', input.disabled);
            });
          }
          g.querySelector('[data-mc-hint]').textContent = ng.querySelector('[data-mc-hint]').textContent;
        });
        const h = this.form.querySelector('[data-mc-hidden]');
        const nh = nextForm.querySelector('[data-mc-hidden]');
        if (h && nh) h.innerHTML = nh.innerHTML;
      }
      const count = this.querySelector('[data-mc-count]');
      const ncount = next.querySelector('[data-mc-count]');
      if (count && ncount) count.textContent = ncount.textContent;
    }

    setBusy(on) {
      const r = this.querySelector('[data-mc-region="results"]');
      if (r) { r.classList.toggle('is-loading', on); r.setAttribute('aria-busy', String(on)); }
      if (this.drawer) this.drawer.classList.toggle('is-updating', on);
    }

    scrollToToolbar() {
      const bar = this.querySelector('.mc-cr-toolbar');
      if (!bar) return;
      const y = Math.max(0, bar.getBoundingClientRect().top + window.scrollY - 16);
      window.scrollTo({ top: y, behavior: document.hidden ? 'auto' : 'smooth' });   // smooth never ends in a hidden tab
    }

    // ── About accordion over the theme's bottom-description section (the redesign's buildAbout) ──
    buildAbout() {
      const cbd = document.querySelector('.collection-main--wrapper .cbd');
      const sec = cbd && cbd.closest('.shopify-section');
      const inner = cbd && cbd.querySelector('.cbd__inner');
      if (!inner || sec.querySelector('.mc-cr-about__head')) return;
      sec.classList.add('mc-cr-about');
      const head = document.createElement('button');
      head.type = 'button';
      head.className = 'mc-cr-about__head';
      head.setAttribute('aria-expanded', 'false');
      head.innerHTML = '<span></span>' + CHEVRON;
      head.firstChild.textContent = this.dataset.aboutLabel;
      head.addEventListener('click', () => {
        const open = !sec.classList.contains('is-open');
        sec.classList.toggle('is-open', open);
        head.setAttribute('aria-expanded', String(open));
        const t = sec.querySelector('.cbd__toggle');              // expand the theme's own clamp once
        if (open && t && t.getAttribute('aria-expanded') === 'false') t.click();
      });
      cbd.insertBefore(head, inner);
    }

    // ── back from a product: scroll to the card that was clicked and outline it (30 min, same state) ──
    returnToCard() {
      const stateKey = () => location.pathname + '?' + this.key;
      this.addEventListener('click', (e) => {
        const a = e.target.closest('.mc-cr-grid a[href*="/products/"]');
        if (!a) return;
        try { sessionStorage.setItem(RETURN_KEY, JSON.stringify({ key: stateKey(), href: a.getAttribute('href').split('?')[0], t: Date.now() })); } catch (err) { /* storage off */ }
      }, true);
      let d = null;
      try { d = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null'); } catch (err) { /* ignore */ }
      if (!d || d.key !== stateKey() || Date.now() - (d.t || 0) > 30 * 60 * 1000) return;
      try { sessionStorage.removeItem(RETURN_KEY); } catch (err) { /* ignore */ }
      const link = [...this.querySelectorAll('.mc-cr-grid a[href*="/products/"]')].find((x) => x.getAttribute('href').split('?')[0] === d.href);
      const card = link && link.closest('.mc-cr-card');
      if (!card) return;
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      setTimeout(() => {
        window.scrollTo(0, Math.max(0, card.getBoundingClientRect().top + window.scrollY - 120));
        card.classList.add('mc-cr-card--returned');
        setTimeout(() => card.classList.remove('mc-cr-card--returned'), 2500);
        if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
      }, 150);
    }
  }

  window.customElements.define('mc-collection', McCollection);
})();
